import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Table, Input, Breadcrumb, Tag, Row, Col, message, Card, Statistic,
  Typography, Space, Tooltip, Empty, Avatar, Progress, Drawer, Select, DatePicker,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  ReloadOutlined, SearchOutlined, WarningOutlined, FileExcelOutlined,
  PhoneOutlined, TeamOutlined, AlertOutlined, EyeOutlined, CarOutlined,
} from '@ant-design/icons';
import { walletService } from '../../common/services';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');
const TYPE = { affiliate: { color: 'blue', label: 'Affilié' }, ambassador: { color: 'purple', label: 'Ambassadeur' } };

export default function ReportingFraude() {
  const [data, setData] = useState({ items: [], totals: { parrains: 0, suspects: 0, fraudTotal: 0 } });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [activity, setActivity] = useState('');
  const [exporting, setExporting] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, parrain: null, loading: false, items: [] });
  const [dActivity, setDActivity] = useState('');
  const [dDates, setDDates] = useState(null);

  const loadDrawerItems = useCallback(async (parrain, act, dts) => {
    if (!parrain) return;
    setDrawer((d) => ({ ...d, loading: true }));
    try {
      const res = await walletService.auditFraude({
        referrerId: parrain.referrerId, suspectOnly: true, limit: 200, sort: '-sosTotal',
        activity: act || undefined,
        start: dts?.[0] ? dts[0].format('YYYY-MM-DD') : undefined,
        end: dts?.[1] ? dts[1].format('YYYY-MM-DD') : undefined,
      });
      setDrawer((d) => ({ ...d, loading: false, items: res.items || [] }));
    } catch (e) {
      message.error('Impossible de charger le détail.');
      setDrawer((d) => ({ ...d, loading: false }));
    }
  }, []);

  const openDetails = (parrain) => {
    setDActivity('');
    setDDates(null);
    setDrawer({ open: true, parrain, loading: true, items: [] });
  };

  // (Re)charge le détail à l'ouverture ou quand les filtres du drawer changent
  useEffect(() => {
    if (drawer.open && drawer.parrain) loadDrawerItems(drawer.parrain, dActivity, dDates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.open, drawer.parrain, dActivity, dDates]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await walletService.fraudeParAffilie({ q, activity, sort: '-fraudTotal', minSuspects: 1 });
      setData(res);
    } catch (e) {
      message.error('Impossible de charger le reporting.');
    } finally {
      setLoading(false);
    }
  }, [q, activity]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await walletService.fraudeParAffilie({ q, activity, sort: '-fraudTotal', minSuspects: 1 });
      const rows = res.items || [];
      if (!rows.length) { message.info('Aucune donnée à exporter.'); return; }
      const sheet = rows.map((r) => ({
        'Type': TYPE[r.referrerType]?.label || r.referrerType,
        'Parrain': r.referrerName || '',
        'Téléphone': r.referrerPhone || '',
        'Statut': r.referrerStatus || (r.referrerActive ? 'actif' : 'inactif'),
        'Ambassadeur (chaîne)': r.ambassadorName || '',
        'Chauffeurs référés (total)': r.totalReferred || 0,
        'Acteurs SOS/Trf': r.actorsCount || 0,
        'Suspects (0 course)': r.suspectsCount || 0,
        'Taux de fraude (%)': r.fraudRate || 0,
        'Fraude SOS': r.fraudSos || 0,
        'Fraude Transfert': r.fraudTransfert || 0,
        'MONTANT TOTAL FRAUDÉ': r.fraudTotal || 0,
        'Opérations SOS': r.sosOps || 0,
        'Opérations Transfert': r.transfertOps || 0,
      }));
      const ws = XLSX.utils.json_to_sheet(sheet);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reporting Fraude');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `reporting-fraude-affilies-${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`);
      message.success(`${rows.length} parrain(s) exporté(s).`);
    } catch (e) {
      message.error("Échec de l'export.");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Parrain',
      key: 'parrain',
      render: (_, r) => (
        <Space>
          <Avatar style={{ backgroundColor: r.fraudRate >= 50 ? '#fff1f0' : '#e6f4ff', color: r.fraudRate >= 50 ? '#cf1322' : '#1677ff' }}>
            {(r.referrerName || '?').charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div>
              <Text strong>{r.referrerName || '—'}</Text>{' '}
              <Tag color={TYPE[r.referrerType]?.color}>{TYPE[r.referrerType]?.label}</Tag>
              {r.referrerActive === false && <Tag>inactif</Tag>}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}><PhoneOutlined /> {r.referrerPhone || '—'}</Text>
            {r.ambassadorName && <div><Text type="secondary" style={{ fontSize: 11 }}>↳ Amb. {r.ambassadorName}</Text></div>}
          </div>
        </Space>
      ),
    },
    {
      title: <Tooltip title="Chauffeurs référés au total"><span><TeamOutlined /> Référés</span></Tooltip>,
      dataIndex: 'totalReferred', key: 'totalReferred', align: 'center',
      sorter: (a, b) => a.totalReferred - b.totalReferred,
      render: (v) => fmt(v),
    },
    {
      title: 'Suspects',
      key: 'suspects', align: 'center',
      sorter: (a, b) => a.suspectsCount - b.suspectsCount,
      render: (_, r) => <Tooltip title={`${r.actorsCount} ont fait SOS/transfert`}><Text strong style={{ color: '#cf1322' }}>{r.suspectsCount}</Text><Text type="secondary"> / {r.actorsCount}</Text></Tooltip>,
    },
    {
      title: 'Taux fraude',
      dataIndex: 'fraudRate', key: 'fraudRate', width: 130,
      sorter: (a, b) => a.fraudRate - b.fraudRate,
      defaultSortOrder: 'descend',
      render: (v) => <Progress percent={v} size="small" status={v >= 50 ? 'exception' : 'normal'} strokeColor={v >= 50 ? '#cf1322' : v >= 25 ? '#fa8c16' : '#1677ff'} />,
    },
    {
      title: 'Montant fraudé',
      key: 'fraudTotal', align: 'right',
      sorter: (a, b) => a.fraudTotal - b.fraudTotal,
      render: (_, r) => (
        <Tooltip title={`SOS ${fmt(r.fraudSos)} · Transfert ${fmt(r.fraudTransfert)}`}>
          <Text strong style={{ color: '#cf1322', fontSize: 15 }}>{fmt(r.fraudTotal)}</Text>
          <div><Text type="secondary" style={{ fontSize: 11 }}>{r.sosOps + r.transfertOps} op.</Text></div>
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      key: 'action', align: 'center',
      render: (_, r) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => openDetails(r)}
          style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
        >
          Détails
        </Button>
      ),
    },
  ];

  // Colonnes du détail (chauffeurs suspects d'un parrain)
  const detailColumns = [
    {
      title: 'Chauffeur', key: 'ch',
      render: (_, r) => (
        <div>
          <Text strong>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</Text>
          {r.isActive === false && <Tag style={{ marginLeft: 4 }}>désactivé</Tag>}
          <div><Text type="secondary" style={{ fontSize: 12 }}><PhoneOutlined /> {r.phone || '—'}</Text></div>
        </div>
      ),
    },
    { title: <><CarOutlined /> Courses</>, key: 'co', align: 'center', render: (_, r) => <Tag color={r.isSuspect ? 'red' : 'green'}>{r.completedCourses}{r.coursesCapped ? '+' : ''} / {r.threshold}</Tag> },
    { title: 'SOS', key: 'sos', align: 'right', render: (_, r) => r.sosCount ? <Text>{fmt(r.sosTotal)} <Text type="secondary" style={{ fontSize: 11 }}>({r.sosCount}×)</Text></Text> : <Text type="secondary">—</Text> },
    { title: 'Transfert', key: 'tr', align: 'right', render: (_, r) => r.transfertCount ? <Text>{fmt(r.transfertTotal)} <Text type="secondary" style={{ fontSize: 11 }}>({r.transfertCount}×)</Text></Text> : <Text type="secondary">—</Text> },
    {
      title: 'Dates', key: 'dt', render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div><Text type="secondary">Compte :</Text> {fmtDate(r.driverCreatedAt)}</div>
          <div><Text type="secondary">SOS/Trf :</Text> {fmtDate(r.lastActivityAt)}</div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 8 }}>
      <Breadcrumb style={{ marginBottom: 16 }} items={[
        { title: <Link to="/dashboard">Accueil</Link> }, { title: 'Wallet' }, { title: 'Reporting Fraude' },
      ]} />

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><AlertOutlined /> Reporting Fraude par Affilié</Title>
          <Text type="secondary">Affiliés/ambassadeurs dont les chauffeurs créés font du SOS/transfert sans course</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Actualiser</Button>
            <Button icon={<FileExcelOutlined />} onClick={handleExport} loading={exporting} disabled={!data.items.length}>Exporter (Excel)</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Parrains impliqués" value={data.totals?.parrains || 0} prefix={<TeamOutlined />} /></Card></Col>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Chauffeurs suspects" value={data.totals?.suspects || 0} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} /></Card></Col>
        <Col xs={24} md={8}><Card size="small"><Statistic title="Montant total fraudé" value={data.totals?.fraudTotal || 0} valueStyle={{ color: '#cf1322' }} suffix="F" /></Card></Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input allowClear placeholder="Rechercher un affilié / ambassadeur…" prefix={<SearchOutlined />} style={{ width: 320 }} value={q} onChange={(e) => setQ(e.target.value)} />
          <Select
            value={activity}
            style={{ width: 180 }}
            onChange={setActivity}
            options={[
              { value: '', label: 'Toute activité' },
              { value: 'sos', label: 'SOS Crédit' },
              { value: 'transfert', label: 'Transfert UV' },
            ]}
          />
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="referrerId"
          loading={loading}
          dataSource={data.items}
          columns={columns}
          locale={{ emptyText: <Empty description="Aucun parrain lié à de la fraude (lance d'abord l'audit)" /> }}
          pagination={{ pageSize: 25, showTotal: (t) => `${t} parrain(s)` }}
        />
      </Card>

      <Drawer
        open={drawer.open}
        width={760}
        onClose={() => setDrawer({ open: false, parrain: null, loading: false, items: [] })}
        title={
          drawer.parrain ? (
            <Space direction="vertical" size={0}>
              <Space>
                <Text strong>{drawer.parrain.referrerName}</Text>
                <Tag color={TYPE[drawer.parrain.referrerType]?.color}>{TYPE[drawer.parrain.referrerType]?.label}</Tag>
              </Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PhoneOutlined /> {drawer.parrain.referrerPhone || '—'}
                {drawer.parrain.ambassadorName && <> · ↳ Amb. {drawer.parrain.ambassadorName}</>}
              </Text>
            </Space>
          ) : 'Détails'
        }
      >
        {drawer.parrain && (
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col span={8}><Card size="small"><Statistic title="Suspects" value={drawer.parrain.suspectsCount} valueStyle={{ color: '#cf1322', fontSize: 18 }} /></Card></Col>
            <Col span={8}><Card size="small"><Statistic title="Sur référés" value={drawer.parrain.totalReferred} valueStyle={{ fontSize: 18 }} /></Card></Col>
            <Col span={8}><Card size="small"><Statistic title="Fraude" value={drawer.parrain.fraudTotal} valueStyle={{ color: '#cf1322', fontSize: 18 }} suffix="F" /></Card></Col>
          </Row>
        )}
        <Text type="secondary">Chauffeurs suspects créés par ce parrain (SOS/transfert sans course) :</Text>
        <Space wrap style={{ margin: '8px 0' }}>
          <Select
            value={dActivity}
            style={{ width: 160 }}
            onChange={setDActivity}
            options={[
              { value: '', label: 'Toute activité' },
              { value: 'sos', label: 'SOS Crédit' },
              { value: 'transfert', label: 'Transfert UV' },
            ]}
          />
          <RangePicker format="DD/MM/YYYY" placeholder={['Activité du', 'au']} value={dDates} onChange={setDDates} />
        </Space>
        <Table
          style={{ marginTop: 8 }}
          rowKey="driverProfilId"
          size="small"
          loading={drawer.loading}
          dataSource={drawer.items}
          columns={detailColumns}
          locale={{ emptyText: <Empty description="Aucun chauffeur suspect" /> }}
          pagination={{ pageSize: 20, showTotal: (t) => `${t} chauffeur(s)` }}
        />
      </Drawer>
    </div>
  );
}
